import { useState } from 'react';
import { Dialog, DialogContent, IconButton, Typography, Box, MobileStepper, Button, Zoom, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RouteIcon from '@mui/icons-material/Route';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import { useLanguage } from '../../../utils/i18n';

const WindingRouteIllustration = ({ active }) => (
  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', my: 2 }}>
    <svg viewBox="0 0 300 120" style={{ width: '100%', maxWidth: 360, overflow: 'visible' }}>
      <path d="M 20,60 C 60,0 100,120 140,60 C 180,0 220,120 260,60" fill="none" stroke="#90caf9" strokeWidth="3" strokeDasharray="6 6" />

      <circle cx="20" cy="60" r="5" fill="#f44336" />
      <text x="20" y="80" fontSize="10" textAnchor="middle" fill="#555" fontWeight="bold">
        Origin
      </text>

      <circle cx="140" cy="60" r="5" fill="#ff9800" />
      <text x="140" y="80" fontSize="10" textAnchor="middle" fill="#555" fontWeight="bold">
        Point 1
      </text>

      <circle cx="260" cy="60" r="5" fill="#4caf50" />
      <text x="260" y="80" fontSize="10" textAnchor="middle" fill="#555" fontWeight="bold">
        Point 2
      </text>

      {active && (
        <g>
          <foreignObject x="-12" y="-12" width="24" height="24">
            <LocalShippingIcon sx={{ color: '#1565c0', fontSize: 24, filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.2))' }} />
          </foreignObject>
          <animateMotion dur="4s" repeatCount="indefinite" path="M 20,60 C 60,0 100,120 140,60 C 180,0 220,120 260,60" />
        </g>
      )}
    </svg>
  </Box>
);

const CalendarIllustration = ({ active }) => (
  <Box sx={{ position: 'relative', width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
    <Zoom in={active} style={{ transitionDelay: active ? '200ms' : '0ms' }}>
      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e3f2fd', borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <RouteIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="caption" display="block" fontWeight="bold">
          Assigned Route
        </Typography>
      </Box>
    </Zoom>
    <Zoom in={active} style={{ transitionDelay: active ? '500ms' : '0ms' }}>
      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e9', borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <CalendarMonthIcon color="success" sx={{ fontSize: 40 }} />
        <Typography variant="caption" display="block" fontWeight="bold">
          Effective Date
        </Typography>
      </Box>
    </Zoom>
  </Box>
);

const ActiveVsHistoricalIllustration = ({ active }) => (
  <Box
    sx={{
      position: 'relative',
      width: '100%',
      height: 140,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        px: 3,
        borderRadius: 2,
        bgcolor: '#e3f2fd',
        transform: active ? 'translateX(0)' : 'translateX(-20px)',
        opacity: active ? 1 : 0,
        transition: 'all 0.8s ease'
      }}
    >
      <LocalShippingIcon color="primary" />
      <Typography fontWeight="bold" color="primary.main">
        Active Route (Current)
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        px: 3,
        borderRadius: 2,
        bgcolor: '#f5f5f5',
        border: '1px dashed #bdbdbd',
        transform: active ? 'translateX(0)' : 'translateX(20px)',
        opacity: active ? 1 : 0,
        transition: 'all 0.8s ease 0.3s'
      }}
    >
      <HistoryIcon sx={{ color: 'text.secondary' }} />
      <Typography fontWeight="bold" color="text.secondary">
        Historical Route (Past)
      </Typography>
    </Box>
  </Box>
);

const VersionControlIllustration = ({ active }) => (
  <Box sx={{ width: '100%', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
    <Box sx={{ textAlign: 'center', p: 1, opacity: active ? 0.5 : 0, transition: 'opacity 1s ease' }}>
      <HistoryIcon color="disabled" sx={{ fontSize: 32 }} />
      <Typography variant="caption" display="block" color="text.secondary">
        V1 (Historical)
      </Typography>
    </Box>
    <Fade in={active} style={{ transitionDelay: '300ms' }}>
      <EditIcon color="action" />
    </Fade>
    <Zoom in={active} style={{ transitionDelay: '600ms' }}>
      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e3f2fd', borderRadius: 2, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
        <RouteIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="caption" display="block" fontWeight="bold" color="primary.main">
          V2 (New Active)
        </Typography>
      </Box>
    </Zoom>
  </Box>
);

const TimelineErrorIllustration = ({ active }) => (
  <Box sx={{ width: '100%', height: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', width: '80%', position: 'relative', height: 40 }}>
      <Box sx={{ flex: 1, height: 4, bgcolor: '#bdbdbd', borderRadius: 2 }} />
      <Zoom in={active} style={{ transitionDelay: '300ms' }}>
        <Box sx={{ position: 'absolute', left: '20%', px: 1, py: 0.5, bgcolor: '#f5f5f5', border: '1px solid #ccc', borderRadius: 1 }}>
          <Typography variant="caption">Past</Typography>
        </Box>
      </Zoom>
      <Zoom in={active} style={{ transitionDelay: '600ms' }}>
        <Box
          sx={{
            position: 'absolute',
            left: '40%',
            px: 1,
            py: 0.5,
            bgcolor: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <WarningAmberIcon color="error" sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="error" fontWeight="bold">
            Overlap Error
          </Typography>
        </Box>
      </Zoom>
    </Box>
  </Box>
);

const HistoryIconIllustration = ({ active, icon: Icon, color, label }) => (
  <Box sx={{ width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Zoom in={active} style={{ transitionDelay: '200ms' }}>
      <Box
        sx={{
          textAlign: 'center',
          p: 3,
          bgcolor: '#f5f5f5',
          borderRadius: '50%',
          border: `2px dashed ${color}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Icon sx={{ fontSize: 40, color: color }} />
        <Typography variant="caption" fontWeight="bold" sx={{ mt: 1, color: color }}>
          {label}
        </Typography>
      </Box>
    </Zoom>
  </Box>
);

export default function GuideRouteDialog({ open, onClose }) {
  const { t } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);

  const slides = [
    {
      title: t('question.guideRouteQ1', 'What is a trip (ritase)?'),
      description: t(
        'answer.guideRouteA1',
        'A trip (ritase) is a vehicle journey from a predefined origin to a predefined destination based on an assigned route. A vehicle is considered to have completed a trip when it travels according to the route assigned to it.'
      ),
      illustration: WindingRouteIllustration
    },
    {
      title: t('question.guideRouteQ2', 'How does the system determine a trip?'),
      description: t(
        'answer.guideRouteA2',
        'The system determines trips based on route assignments. Each route defines the origin, destination, assigned vehicles, and assignment date. A vehicle journey is recognized as a trip when it follows the assigned route during its valid period.'
      ),
      illustration: WindingRouteIllustration
    },
    {
      title: t('question.guideRouteQ3', 'What is a Route Assignment?'),
      description: t(
        'answer.guideRouteA3',
        'A Route Assignment determines which vehicles are assigned to a specific route. When creating a route, users specify the route, the assigned vehicles, and the Assignment Date.'
      ),
      illustration: CalendarIllustration
    },
    {
      title: t('question.guideRouteQ4', 'Purpose of the Assignment Date'),
      description: t(
        'answer.guideRouteA4',
        'The Assignment Date defines when a route assignment becomes effective. When the system calculates or searches for trips within a specific period, it uses the route assignment that was valid during that period.'
      ),
      illustration: CalendarIllustration
    },
    {
      title: t('question.guideRouteQ5', 'Active vs Historical Route'),
      description: t(
        'answer.guideRouteA5',
        'An Active Route is the latest route assignment currently used for a vehicle. A Historical Route is a previous route assignment that is no longer active but is retained to preserve historical records.'
      ),
      illustration: ActiveVsHistoricalIllustration
    },
    {
      title: t('question.guideRouteQ6', 'Why move previous routes to Historical?'),
      description: t(
        'answer.guideRouteA6',
        'An edited route is treated as a new version. The previous route is deactivated and retained as a Historical Route, while a new route assignment is created based on the updated information.'
      ),
      illustration: VersionControlIllustration
    },
    {
      title: t('question.guideRouteQ7', 'Does editing modify the original data?'),
      description: t(
        'answer.guideRouteA7',
        'No. Editing an Active Route creates a new version of the route assignment. The previous version is retained as a Historical Route to preserve assignment history.'
      ),
      illustration: VersionControlIllustration
    },
    {
      title: t('question.guideRouteQ8', 'Why can’t Assignment Date be earlier?'),
      description: t(
        'answer.guideRouteA8',
        'Because it represents when the assignment becomes effective. Changing an Active Route to an earlier date could cause new and previous route versions to overlap in the historical timeline, resulting in ambiguous trip detection.'
      ),
      illustration: TimelineErrorIllustration
    },
    {
      title: t('question.guideRouteQ9', 'Missed a route for a past date?'),
      description: t(
        'answer.guideRouteA9',
        'If a route needs to be added for a past period, it should be added through Historical Route to complete historical route records.'
      ),
      illustration: (props) => (
        <HistoryIconIllustration
          {...props}
          icon={AddCircleOutlineIcon}
          color="#ff9800"
          label={t('general.addToHistory', 'Add to History')}
        />
      )
    },
    {
      title: t('question.guideRouteQ10', 'When to use Active vs Historical?'),
      description: t(
        'answer.guideRouteA10',
        'Use Active Route when creating or updating the latest route assignment for current/future operations. Use Historical Route when viewing previous assignments or adding route records for a past period.'
      ),
      illustration: ActiveVsHistoricalIllustration
    },
    {
      title: t('question.guideRouteQ11', 'System logic for historical trips?'),
      description: t(
        'answer.guideRouteA11',
        'The system checks the search period and determines which route assignment was valid during that period using Historical Route records.'
      ),
      illustration: (props) => (
        <HistoryIconIllustration {...props} icon={SearchIcon} color="#2196f3" label={t('general.searchPastTrips', 'Search Past Trips')} />
      )
    },
    {
      title: t('question.guideRouteQ12', 'Why is Historical Route needed?'),
      description: t(
        'answer.guideRouteA12',
        'It preserves previous route assignments. Without it, changes to Active Routes could overwrite past info, making it difficult to accurately identify the route applicable to past trips.'
      ),
      illustration: (props) => (
        <HistoryIconIllustration {...props} icon={LockIcon} color="#4caf50" label={t('general.preserveData', 'Preserve Data')} />
      )
    }
  ];

  const maxSteps = slides.length;

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const currentSlide = slides[activeStep];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
          bgcolor: '#ffffff'
        }
      }}
    >
      <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
        <IconButton onClick={onClose} sx={{ bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: '#f5f5f5' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
            pt: 5,
            pb: 3,
            px: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: 420
          }}
        >
          {/* Progress Indicator Text */}
          <Typography variant="overline" color="primary.main" fontWeight="bold" sx={{ letterSpacing: 1, mb: 1 }}>
            {activeStep + 1} of {maxSteps}
          </Typography>

          {/* Illustration Container */}
          <Box sx={{ width: '100%', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            {slides.map((slide, index) => (
              <Box key={index} sx={{ display: activeStep === index ? 'block' : 'none', width: '100%' }}>
                {activeStep === index && <slide.illustration active={true} />}
              </Box>
            ))}
          </Box>

          {/* Text Content */}
          <Fade in={true} key={activeStep}>
            <Box sx={{ textAlign: 'center', minHeight: 140 }}>
              <Typography variant="h5" fontWeight="800" sx={{ mb: 1.5, color: '#1a2027', fontSize: '1.25rem' }}>
                {currentSlide.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#455a64', lineHeight: 1.6, px: 1 }}>
                {currentSlide.description}
              </Typography>
            </Box>
          </Fade>
        </Box>

        {/* Stepper Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #f0f0f0',
            bgcolor: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Button size="small" onClick={handleBack} disabled={activeStep === 0} sx={{ fontWeight: 'bold' }}>
            <KeyboardArrowLeft />
            Back
          </Button>

          <MobileStepper
            variant="progress"
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            sx={{
              flex: 1,
              px: 3,
              bgcolor: 'transparent',
              '& .MuiLinearProgress-root': { width: '100%', height: 6, borderRadius: 3 }
            }}
            nextButton={null}
            backButton={null}
          />

          <Button size="small" onClick={activeStep === maxSteps - 1 ? onClose : handleNext} sx={{ fontWeight: 'bold' }}>
            {activeStep === maxSteps - 1 ? 'Got it' : 'Next'}
            {activeStep !== maxSteps - 1 && <KeyboardArrowRight />}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
